import React from 'react';
import '../../../../style/CompanyFormTabs.css';

const CompanyTabs = ({ tabs, activeTab, setActiveTab, tabValidation, errors }) => {
  const handleTabClick = (tabIndex) => {
    // Allow clicking on current tab and previous tabs
    // Allow clicking on next tab only if current tab is valid
    if (tabIndex <= activeTab || (tabIndex === activeTab + 1 && tabValidation[activeTab])) {
      setActiveTab(tabIndex);
    }
  };

  const getTabStatus = (index) => {
    if (tabValidation[index]) {
      return 'completed';
    } else if (index === activeTab) {
      return 'current';
    } else if (hasTabErrors(index)) {
      return 'error';
    }
    return 'pending';
  };

  const hasTabErrors = (index) => {
    const tabErrorKeys = {
      0: ['companyName', 'companyWebsite', 'organisationSize', 'aboutUs', 'companyLogo'],
      1: ['address1', 'state', 'city'],
      2: ['contactPersonName', 'contactPersonNumber', 'contactPersonEmail']
    };
    
    return Object.keys(errors).some(key => tabErrorKeys[index]?.includes(key));
  };

  const getStepIcon = (index, status) => {
    switch (status) {
      case 'completed':
        return <i className="bi bi-check-circle-fill text-success"></i>;
      case 'current':
        return <span className="fw-bold text-white">{index + 1}</span>;
      case 'error':
        return <i className="bi bi-exclamation-circle-fill text-danger"></i>;
      default:
        return <span className="fw-bold text-muted">{index + 1}</span>;
    }
  };

  const getStepClasses = (index, status) => {
    const baseClasses = 'd-flex align-items-center justify-content-center rounded-circle';
    
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-success text-white`;
      case 'current':
        return `${baseClasses} bg-primary text-white`;
      case 'error':
        return `${baseClasses} bg-light border border-danger`;
      default:
        return `${baseClasses} bg-light border text-muted`;
    }
  };

  const getConnectorClasses = (index) => {
    return tabValidation[index] ? 'bg-success' : 'bg-light';
  };

  return (
    <div className="company-tabs-container">
      {/* Step Indicator Circles */}
      <div className="step-indicator">
        <div className="step-indicator-wrapper">
          {tabs.map((tab, index) => {
            const status = getTabStatus(index);
            const isClickable = index <= activeTab || (index === activeTab + 1 && tabValidation[activeTab]);
            
            return (
              <div key={tab.id} className="d-flex align-items-center">
                <div
                  className={`step-circle ${status} ${!isClickable ? 'disabled' : ''}`}
                  onClick={() => isClickable && handleTabClick(index)}
                  title={`Step ${index + 1}: ${tab.title}`}
                >
                  {tabValidation[index] ? (
                    <i className="bi bi-check"></i>
                  ) : (
                    getStepIcon(index, status)
                  )}
                </div>
                {index < tabs.length - 1 && (
                  <div
                    className={`step-connector ${tabValidation[index] ? 'completed' : 'pending'}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tab Navigation List */}
      <ul className="nav nav-tabs nav-justified" role="tablist">
        {tabs.map((tab, index) => {
          const status = getTabStatus(index);
          const isClickable = index <= activeTab || (index === activeTab + 1 && tabValidation[activeTab]);
          
          return (
            <li key={tab.id} className="nav-item">
              <button
                className={`nav-link d-flex align-items-center justify-content-center ${
                  index === activeTab ? 'active' : ''
                } ${!isClickable ? 'disabled' : ''}`}
                onClick={() => isClickable && handleTabClick(index)}
                disabled={!isClickable}
              >
                <span className="me-2">
                  {status === 'completed' && <i className="bi bi-check-circle-fill text-success"></i>}
                  {status === 'current' && <i className="bi bi-circle-fill text-primary"></i>}
                  {status === 'error' && <i className="bi bi-exclamation-circle-fill text-danger"></i>}
                  {status === 'pending' && <i className="bi bi-circle text-muted"></i>}
                </span>
                <span className="d-none d-md-inline">{tab.title}</span>
                <span className="d-md-none">{index + 1}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CompanyTabs;